import { zodResolver } from "@hookform/resolvers/zod";
import { getConfig } from "@stryker/core/api/jobs";
import type { LocalFile } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { type ChangeEvent, Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { groupBy, prop, unique, uniqueBy } from "remeda";
import { z } from "zod";
import FileUploader from "@/components/FileUploader";
import AttachedDocument from "@/components/job/AttachedDocument";
import { Button, Checkbox, ImportantNote, Label, TextInput, Typography, useToast } from "@/components/ui";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { JOB_TYPES } from "@/constants/job";
import { additionalNotesQueryOptions } from "@/hooks/useAdditionalNotes";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { cn } from "@/lib/utils";

const QueryKeys = {
  JobsConfig: "jobs-config",
} as const;

const PLACEHOLDER_ITEMS = [
  { id: "pinhole", label: "Pinhole Defect" },
  { id: "slot", label: "Slot Defect" },
  { id: "burial", label: "Burial Loads" },
  { id: "axialLoads", label: "Axial Loads" },
  { id: "additionalForces", label: "Additional Forces and Moments" },
  { id: "externalPressure", label: "External Pressure Vacuum" },
  { id: "pressureCycles", label: "Pressure Cycles" },
  { id: "continuousStrains", label: "Continuous Strains" },
  { id: "operatingConditions", label: "Operating Conditions" },
  { id: "excludeComponentStrength", label: "Exclude Component Strength" },
  { id: "nonCompliant", label: "Non Compliant Repair" },
];

const checkOptions = [
  { value: "bend", label: "Bends" },
  { value: "tee_value_nozzles_or_90_with_dummy_leg", label: "Tee, Nozzles, or 90 with dummy leg" },
  { value: "flange", label: "Flange or Valve" },
  { value: "reducer", label: "Reducer" },
  { value: "end_cap_or_vessel_dome", label: "End Cap or Vessel Dome" },
  { value: "vessel_supports_saddles_or_attachments", label: "Vessel Supports, Saddles, or Attachments" },
  { value: "none", label: "None of the Above" },
];

interface JobData {
  job_number: string;
  plant_name: string;
  service_name: string;
  repair_system: string;
  surface_preparation: string;
  branch: string;
  ut_readings: string;
  extra_layers: string;
}

interface DesignData {
  design_pressure: string;
  design_temperature: string;
  unit_for_design_temperature: string;
}

interface ComponentData {
  component_nps: string;
  component_schedule?: string;
  component_diameter?: string;
  unit_for_component_diameter?: string;
  component_thickness?: string;
  unit_for_component_thickness?: string;
  is_flanges: boolean;
}

interface DefectData {
  defect_length_total: string;
  unit_for_defect_length: string;
  defect_length_wrap: string;
  unit_for_defect_length_wrap: string;
}

type NoteFormData = { custom_name_of_note?: string; custom_note?: string };
type NoteSchema = NoteFormData & { id: string };

interface ProModeProps {
  onCancel?: () => void;
  onSaveDraft?: (
    submitData: FormData | Record<string, unknown>,
    isMultipart: boolean,
    displayData: Record<string, unknown>
  ) => void;
  onRunCalculations?: (
    submitData: FormData | Record<string, unknown>,
    isMultipart: boolean,
    displayData: Record<string, unknown>
  ) => void;
  isPending?: boolean;
  canRunCalculations?: boolean;
}

function NoteComponent({
  data,
  onSave,
  onDelete,
  allNotesNames,
}: {
  data: NoteSchema;
  onSave: (data: NoteFormData) => void;
  onDelete: () => void;
  allNotesNames: string[];
}) {
  const noteSchema = z
    .object({
      custom_name_of_note: z.string().optional(),
      custom_note: z.string().optional(),
    })
    .superRefine((data, context) => {
      if (data.custom_name_of_note && !data.custom_note) {
        context.addIssue({
          code: "custom",
          message: "custom note is required when custom name of note is present.",
          path: ["custom_note"],
        });
      }

      if (data.custom_note && !data.custom_name_of_note) {
        context.addIssue({
          code: "custom",
          message: "custom name of note is required when custom note is present.",
          path: ["custom_name_of_note"],
        });
      }

      if (data.custom_name_of_note && allNotesNames.includes(data.custom_name_of_note)) {
        context.addIssue({
          code: "custom",
          message: "Name of note must be unique",
          path: ["custom_name_of_note"],
        });
      }
    });

  type LocalNoteSchema = z.infer<typeof noteSchema>;

  const {
    register,
    formState: { errors },
    getValues,
  } = useForm<LocalNoteSchema>({
    resolver: zodResolver(noteSchema),
    defaultValues: data,
    mode: "onChange",
  });

  const handleBlur = () => {
    onSave(getValues());
  };

  const noteRegister = register("custom_note");
  const nameRegister = register("custom_name_of_note");

  return (
    <div>
      <form className="my-4 flex flex-col">
        <div className="flex flex-wrap items-end gap-4">
          <TextInput
            className="flex-1 basis-full md:basis-56"
            label="Note"
            {...noteRegister}
            onBlur={(event) => {
              noteRegister.onBlur(event);
              handleBlur();
            }}
            error={errors.custom_note?.message}
          />
          <TextInput
            className="flex-1 basis-full md:basis-56"
            label="Name of note"
            {...nameRegister}
            onBlur={(event) => {
              nameRegister.onBlur(event);
              handleBlur();
            }}
            error={errors.custom_name_of_note?.message}
          />
          <Button
            variant="outline"
            size="icon"
            className="mt-6 self-end"
            type="button"
            onClick={onDelete}
            aria-label="Delete note"
            title="Delete note"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

const initialJobData: JobData = {
  job_number: "",
  plant_name: "",
  service_name: "",
  repair_system: "",
  surface_preparation: "",
  branch: "",
  ut_readings: "",
  extra_layers: "",
};

const initialDesignData: DesignData = {
  design_pressure: "",
  design_temperature: "",
  unit_for_design_temperature: "Fahrenheit",
};

const initialComponent: ComponentData = {
  component_nps: "",
  component_diameter: "",
  unit_for_component_diameter: "Inches",
  component_thickness: "",
  unit_for_component_thickness: "Inches",
  component_schedule: "",
  is_flanges: false,
};

const initialDefect: DefectData = {
  defect_length_total: "",
  unit_for_defect_length: "Inches",
  defect_length_wrap: "",
  unit_for_defect_length_wrap: "Inches",
};

const temperatureUnitsOptions = [
  { title: "Celsius", value: "Celsius" },
  { title: "Fahrenheit", value: "Fahrenheit" },
];

const measureUnitOptions = [
  { title: "Inches", value: "Inches" },
  { title: "Feet", value: "Feet" },
];

export default function ProMode({
  onCancel,
  onSaveDraft,
  onRunCalculations,
  isPending = false,
  canRunCalculations = true,
}: ProModeProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, any>>({});
  const [jobData, setJobData] = useState<JobData>(initialJobData);
  const [designData, setDesignData] = useState<DesignData>(initialDesignData);
  const [componentMaterial, setComponentMaterial] = useState<string>("");
  const [componentOptions, setComponentOptions] = useState<string[]>([]);
  const [components, setComponents] = useState<ComponentData[]>([initialComponent]);
  const [defects, setDefects] = useState<DefectData[]>([initialDefect]);
  const [additional_notes, setAdditional_notes] = useState<string[]>([]);
  const [noteComponents, setNoteComponents] = useState<NoteSchema[]>([]);
  const [documents, setDocuments] = useState<LocalFile[]>([]);
  const [referenceDocuments, setReferenceDocuments] = useState<LocalFile[]>([]);
  const [job_name, setJob_name] = useState<string>("");
  const [repair_length_options, setRepair_length_options] = useState<string[]>([]);
  const [repair_length_repair_kit_sizes, setRepair_length_repair_kit_sizes] = useState<number[]>([1]); // Default to [1] to match example
  const { toast } = useToast();
  const {
    repairSystemOptions,
    surfacePreparationOptions,
    npsOptions,
    componentMaterialGroups,
    getScheduleOptions,
    getKitOptions,
    repairSystems, // Assuming this has id, title, repair_system_max_temp
  } = useConfigOptions();
  const { data: { mappedNotesOptions = [], existingNotesNames = [] } = {} } = useQuery({
    ...additionalNotesQueryOptions("composite"),
    select: (data) => ({
      mappedNotesOptions: data?.additional_notes?.map((note) => ({ label: note.name, value: String(note.id) })) ?? [],
      existingNotesNames: data?.additional_notes?.map(({ name }) => name) ?? [],
    }),
  });

  // Fetch grouped repair options
  const { data: groupedRepairOptions = [] } = useQuery({
    queryKey: [QueryKeys.JobsConfig],
    queryFn: getConfig,
    staleTime: 1000000,
    select: (response) => {
      // Same item name can appear multiple times with different units (e.g. per gallon vs per quart).
      // Unit doesn't matter for this checklist, so we only keep the first occurrence of each name.
      const grouped = groupBy(uniqueBy(response.repair_options, prop("name")), prop("type"));
      return Object.entries(grouped);
    },
  });

  // Add custom option to npsOptions if not present
  const npsWithCustom = npsOptions.some((opt) => opt.value === "custom")
    ? npsOptions
    : [...npsOptions, { title: "Custom", value: "custom" }];

  const kitsOptions = getKitOptions(Number(jobData.repair_system));

  const newNoteNames = noteComponents.map(({ custom_name_of_note }) => custom_name_of_note).filter(Boolean) as string[];
  const allNotesNames = unique([...newNoteNames, ...existingNotesNames]);

  const updateJobData = (field: keyof JobData, value: string) => {
    setJobData((prev) => ({ ...prev, [field]: value }));
  };

  const updateDesignData = (field: keyof DesignData, value: string) => {
    setDesignData((prev) => ({ ...prev, [field]: value }));
  };

  const updateComponent = (index: number, field: keyof ComponentData, value: any) => {
    setComponents((prev) => prev.map((comp, i) => (i === index ? { ...comp, [field]: value } : comp)));
  };

  const updateDefect = (index: number, field: keyof DefectData, value: string) => {
    setDefects((prev) => prev.map((def, i) => (i === index ? { ...def, [field]: value } : def)));
  };

  const addComponent = () => {
    setComponents((prev) => [...prev, initialComponent]);
    setDefects((prev) => [...prev, initialDefect]);
  };

  const removeComponent = (index: number) => {
    if (components.length <= 1) return;
    setComponents((prev) => prev.filter((_, i) => i !== index));
    setDefects((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddNote = () => {
    setNoteComponents((prev) => [...prev, { id: crypto.randomUUID(), custom_name_of_note: "", custom_note: "" }]);
  };

  const handleNoteChange = (id: string, data: NoteFormData) => {
    setNoteComponents((prev) => prev.map((note) => (note.id === id ? { ...note, ...data } : note)));
  };

  const handleNoteDelete = (id: string) => {
    setNoteComponents((prev) => prev.filter((note) => note.id !== id));
  };

  const toggleItem = (id: string, checked: boolean) => {
    if (id === "nonCompliant") {
      if (checked) {
        setSelectedItems(["nonCompliant"]);
        setInputValues({ nonCompliant: { desired_layer_count: "" } });
      } else {
        setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
        setInputValues((prev) => {
          const newValues = { ...prev };
          delete newValues[id];
          return newValues;
        });
      }
      return;
    }

    if (checked) {
      if (selectedItems.includes("nonCompliant")) {
        return; // Prevent selecting others when nonCompliant is active
      }
      if (!selectedItems.includes(id)) {
        setSelectedItems((prev) => [...prev, id]);
      }
      let initialData: any = {};
      if (id === "pinhole") {
        initialData = { pinhole_diameter: "" };
      } else if (id === "slot") {
        initialData = { slot_diameter: "" };
      } else if (id === "burial") {
        initialData = { buried_depth: "", unit_for_buried_check: "Inches" };
      } else if (id === "axialLoads") {
        initialData = {};
      } else if (id === "additionalForces") {
        initialData = {
          applied_sheared_load: "",
          applied_axial_load: "",
          applied_tortional_moment: "",
          applied_axial_moment: "",
        };
      } else if (id === "externalPressure") {
        initialData = { external_pressure_vacuum: "" };
      } else if (id === "pressureCycles") {
        initialData = {
          number_of_cycles: "",
          min_cycle_pressure: "",
          max_cycle_pressure: "",
        };
      } else if (id === "continuousStrains") {
        initialData = {};
      } else if (id === "operatingConditions") {
        initialData = {
          operating_pressure: "",
          operating_temperature: "",
          unit_for_operating_temperature: "Fahrenheit",
          installation_temperature: "",
          unit_for_installation_temperature: "Fahrenheit",
        };
      } else if (id === "excludeComponentStrength") {
        initialData = {};
      }
      if (!inputValues[id]) {
        setInputValues((prev) => ({ ...prev, [id]: initialData }));
      }
    } else {
      if (selectedItems.includes(id)) {
        setSelectedItems((prev) => prev.filter((itemId) => itemId !== id));
      }
      setInputValues((prev) => {
        const newValues = { ...prev };
        delete newValues[id];
        return newValues;
      });
    }
  };

  const updateInput = (id: string, field: string, value: string) => {
    setInputValues((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  // Prepare data matching Standard Mode structure
  const prepareSubmitData = (is_draft: boolean, _forDisplay = false) => {
    const pinholeData = selectedItems.includes("pinhole") ? inputValues.pinhole : undefined;
    const slotData = selectedItems.includes("slot") ? inputValues.slot : undefined;
    const burialData = selectedItems.includes("burial") ? inputValues.burial : undefined;
    const additionalForcesData = selectedItems.includes("additionalForces") ? inputValues.additionalForces : undefined;
    const externalPressureData = selectedItems.includes("externalPressure") ? inputValues.externalPressure : undefined;
    const pressureCyclesData = selectedItems.includes("pressureCycles") ? inputValues.pressureCycles : undefined;
    const operatingConditionsData = selectedItems.includes("operatingConditions")
      ? inputValues.operatingConditions
      : undefined;
    const nonCompliantData = selectedItems.includes("nonCompliant") ? inputValues.nonCompliant : undefined;

    // Build components_data with nulls for non-custom
    const componentsData = components
      .map((comp, index) => {
        const defect = defects[index];
        if (!comp.component_nps && !defect.defect_length_total) return null; // Skip fully empty
        return {
          component_nps: comp.component_nps,
          component_diameter: comp.component_nps === "custom" ? comp.component_diameter || null : null,
          unit_for_component_diameter: comp.component_nps === "custom" ? comp.unit_for_component_diameter : null,
          component_thickness:
            comp.is_flanges || comp.component_nps !== "custom" ? null : comp.component_thickness || null,
          unit_for_component_thickness:
            comp.is_flanges || comp.component_nps !== "custom" ? null : comp.unit_for_component_thickness,
          component_schedule: comp.component_nps !== "custom" ? comp.component_schedule : null,
          is_flanges: comp.is_flanges,
          defect_length_total: defect.defect_length_total || null,
          unit_for_defect_length: defect.unit_for_defect_length,
          defect_length_wrap: defect.defect_length_wrap || null,
          unit_for_defect_length_wrap: defect.unit_for_defect_length_wrap,
        };
      })
      .filter(Boolean); // Remove skipped empty

    // Defaults to match example
    const componentOptionsFinal = componentOptions.length > 0 ? componentOptions : ["none"];
    const desiredLayerCountOptions = nonCompliantData?.desired_layer_count
      ? [nonCompliantData.desired_layer_count]
      : ["none"];
    const hasUtReadings = !!jobData.ut_readings;
    const hasExtraLayers = !!jobData.extra_layers;
    const repairSystemMaxTemp = repairSystems?.find(
      (r) => r.id === Number(jobData.repair_system)
    )?.repair_system_max_temp;

    const baseData: Record<string, unknown> = {
      ...(job_name.trim() && { job_name }),
      is_draft,
      job_type: JOB_TYPES.composite,
      job_product_type: "composite",
      job_number: jobData.job_number || null,
      plant_name: jobData.plant_name || null,
      service_name: jobData.service_name || null,
      repair_system: Number(jobData.repair_system) || null,
      surface_preparation: Number(jobData.surface_preparation) || null,
      branch: jobData.branch || null,
      design_pressure: Number(designData.design_pressure) || null,
      design_temperature: Number(designData.design_temperature) || null,
      unit_for_design_temperature: designData.unit_for_design_temperature,
      unit_for_operating_temp_temperature: "Fahrenheit",
      unit_for_installation_temperature: "Fahrenheit",
      component_options: componentOptionsFinal,
      components_data: componentsData.length > 0 ? componentsData : null,
      component_material: Number(componentMaterial) || null,
      defects:
        componentsData.length > 0
          ? componentsData.map((c) => ({
              defect_length_total: c.defect_length_total,
              unit_for_defect_length: c.unit_for_defect_length as string,
              defect_length_wrap: c.defect_length_wrap,
              unit_for_defect_length_wrap: c.unit_for_defect_length_wrap as string,
            }))
          : null,
      repair_length_options,
      repair_length_repair_kit_sizes,
      desired_layer_count_options: desiredLayerCountOptions,
      do_you_have_ut_reading_info: hasUtReadings ? "yes" : "no",
      ...(hasUtReadings && { ut_reading_info: Number(jobData.ut_readings) }),
      calculate_at_worst_case: "no",
      component_leaking: "no",
      component_patched_or_repaired: "no",
      is_component_shutdown_during_repair: "no",
      add_extraLayers: hasExtraLayers ? "yes" : "no",
      ...(hasExtraLayers && { extra_layers: Number(jobData.extra_layers) }),
      do_you_wish_to_answer_more_questions: "no",
      ...(repairSystemMaxTemp && { repair_system_max_temp: repairSystemMaxTemp }),
      notes: {
        additional_notes: additional_notes.map(Number),
        new_notes: noteComponents.filter((note) => note.custom_name_of_note && note.custom_note),
      },
      // Optional flat fields if selected
      ...(pinholeData && { pinhole_diameter: pinholeData.pinhole_diameter || null }),
      ...(slotData && { slot_diameter: slotData.slot_diameter || null }),
      ...(burialData && {
        buried_depth: burialData.buried_depth || null,
        unit_for_buried_check: burialData.unit_for_buried_check,
      }),
      ...(selectedItems.includes("axialLoads") && {
        design_axial_loads: "yes",
        is_component_straight_line: "yes",
      }),
      ...(additionalForcesData && {
        applied_sheared_load: Number(additionalForcesData.applied_sheared_load) || null,
        applied_axial_load: Number(additionalForcesData.applied_axial_load) || null,
        applied_tortional_moment: Number(additionalForcesData.applied_tortional_moment) || null,
        applied_axial_moment: Number(additionalForcesData.applied_axial_moment) || null,
      }),
      ...(externalPressureData && {
        external_pressure_vacuum: Number(externalPressureData.external_pressure_vacuum) || null,
      }),
      ...(pressureCyclesData && {
        number_of_cycles: Number(pressureCyclesData.number_of_cycles) || null,
        min_cycle_pressure: Number(pressureCyclesData.min_cycle_pressure) || null,
        max_cycle_pressure: Number(pressureCyclesData.max_cycle_pressure) || null,
      }),
      ...(selectedItems.includes("continuousStrains") && {
        do_you_want_to_design_for_continous_long_term_strains: "yes",
      }),
      ...(operatingConditionsData && {
        operating_pressure: Number(operatingConditionsData.operating_pressure) || null,
        operating_temperature: Number(operatingConditionsData.operating_temperature) || null,
        unit_for_operating_temp_temperature: operatingConditionsData.unit_for_operating_temperature,
        installation_temperature: Number(operatingConditionsData.installation_temperature) || null,
        unit_for_installation_temperature: operatingConditionsData.unit_for_installation_temperature,
      }),
      ...(selectedItems.includes("excludeComponentStrength") && {
        do_you_want_to_completely_exclude_the_components_strength_for_design: "yes",
      }),
      ...(nonCompliantData && {
        do_you_want_a_non_compliant_repair: "yes",
        desired_layer_count: nonCompliantData.desired_layer_count || null,
      }),
    };

    // Clean: delete undefined, "", empty arrays; keep null and non-empty objects/arrays
    const payload: Record<string, unknown> = { ...baseData };
    for (const key in payload) {
      if (
        payload[key] === undefined ||
        payload[key] === "" ||
        (Array.isArray(payload[key]) && (payload[key] as unknown[]).length === 0)
      ) {
        delete payload[key];
      }
      // Clean empty single notes
      if (
        key === "new_notes" &&
        Array.isArray(payload[key]) &&
        (payload[key] as unknown[]).length === 1 &&
        typeof (payload[key] as unknown[])[0] === "object" &&
        (payload[key] as unknown[])[0] !== null &&
        ((payload[key] as unknown[])[0] as { custom_name_of_note?: string; custom_note?: string })
          .custom_name_of_note === "" &&
        ((payload[key] as unknown[])[0] as { custom_name_of_note?: string; custom_note?: string }).custom_note === ""
      ) {
        delete payload[key];
      }
      // Clean empty single components_data
      if (
        key === "components_data" &&
        Array.isArray(payload[key]) &&
        (payload[key] as unknown[]).length === 1 &&
        typeof (payload[key] as unknown[])[0] === "object" &&
        (payload[key] as unknown[])[0] !== null &&
        Object.values((payload[key] as unknown[])[0] as Record<string, unknown>).every(
          (v: unknown) => v === null || v === "" || v === undefined
        )
      ) {
        delete payload[key];
      }
    }

    const hasAttachments = documents.length > 0 || referenceDocuments.length > 0;
    let submitData: FormData | Record<string, unknown> = payload;
    if (hasAttachments) {
      const formData = new FormData();
      for (const property in payload) {
        const value = payload[property];
        if (Array.isArray(value) && value.filter(Boolean).length > 0) {
          (value as unknown[]).forEach((v: unknown) => {
            formData.append(property, JSON.stringify(v));
          });
        } else if (!Array.isArray(value) && value !== null && value !== undefined) {
          formData.append(property, String(value));
        }
      }
      documents.forEach((doc) => {
        formData.append("attached_documents", doc.file);
      });
      referenceDocuments.forEach((doc) => {
        formData.append("reference_documents", doc.file);
      });
      submitData = formData;
    }

    return { submitData, displayData: payload, isMultipart: hasAttachments };
  };

  // Basic validation
  const isValidForDraft = () =>
    job_name.trim() !== "" || jobData.job_number || designData.design_pressure || components.length > 0;
  const isValidForSubmit = () =>
    jobData.job_number !== "" &&
    designData.design_pressure !== "" &&
    componentMaterial !== "" &&
    componentOptions.length > 0 &&
    components.every((c) => c.component_nps !== "") &&
    defects.every((d) => d.defect_length_total !== "") &&
    repair_length_repair_kit_sizes.length > 0;

  const handleSaveDraft = () => {
    if (!isValidForDraft()) {
      toast({ variant: "destructive", description: "Please fill at least one section." });
      return;
    }
    const { submitData, displayData, isMultipart } = prepareSubmitData(true);
    onSaveDraft?.(submitData, isMultipart, displayData);
  };

  const handleRunCalculations = () => {
    if (!isValidForSubmit()) {
      toast({ variant: "destructive", description: "Please fill all required fields." });
      return;
    }
    const { submitData, displayData, isMultipart } = prepareSubmitData(false);
    onRunCalculations?.(submitData, isMultipart, displayData);
  };

  const handleCancel = () => {
    // Reset state
    setSelectedItems([]);
    setInputValues({});
    setJobData(initialJobData);
    setDesignData(initialDesignData);
    setComponentMaterial("");
    setComponentOptions([]);
    setComponents([initialComponent]);
    setDefects([initialDefect]);
    setRepair_length_options([]);
    setRepair_length_repair_kit_sizes([1]);
    setAdditional_notes([]);
    setNoteComponents([]);
    setDocuments([]);
    setReferenceDocuments([]);
    setJob_name("");
    onCancel?.();
  };

  const isNonCompliantSelected = selectedItems.includes("nonCompliant");

  return (
    <div className="mt-9 rounded-lg bg-gray-50 p-6">
      <Typography variant="text-lg" weight="semibold" className="mb-6 text-center">
        Pro Mode: Provide Details
      </Typography>
      <ImportantNote className="mb-6">
        Provide details for the required sections below. Optional defects can be selected to include additional details.
      </ImportantNote>

      <div className="mb-6 flex flex-col gap-6 lg:flex-row">
        <div className="flex-1 space-y-6">
          {/* Always-visible main sections */}
          {/* Job Details */}
          <div className="rounded border bg-white p-4">
            <Typography variant="text-small" weight="medium" className="mb-3">
              Job Details
            </Typography>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TextInput
                label="Job Number"
                placeholder="Job number"
                value={jobData.job_number}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateJobData("job_number", e.target.value)}
              />
              <TextInput
                label="Plant Name"
                placeholder="Plant name"
                value={jobData.plant_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateJobData("plant_name", e.target.value)}
              />
              <TextInput
                label="Service"
                placeholder="Service name"
                value={jobData.service_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateJobData("service_name", e.target.value)}
              />
              <div className="md:col-span-1">
                <Typography variant="text-small" weight="medium" className="mb-1">
                  Repair System
                </Typography>
                <Select value={jobData.repair_system} onValueChange={(v) => updateJobData("repair_system", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Repair system" />
                  </SelectTrigger>
                  <SelectContent>
                    {repairSystemOptions.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1">
                <Typography variant="text-small" weight="medium" className="mb-1">
                  Surface Preparation
                </Typography>
                <Select
                  value={jobData.surface_preparation}
                  onValueChange={(v) => updateJobData("surface_preparation", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Surface preparation" />
                  </SelectTrigger>
                  <SelectContent>
                    {surfacePreparationOptions.map((option) => (
                      <SelectItem key={option.value} value={String(option.value)}>
                        {option.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <TextInput
                label="Branch"
                placeholder="Branch"
                value={jobData.branch}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateJobData("branch", e.target.value)}
              />
              <TextInput
                label="Job Repair Identifier"
                placeholder="Job Repair Identifier"
                value={job_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setJob_name(e.target.value)}
              />
              {!isNonCompliantSelected && (
                <>
                  <TextInput
                    label="UT Readings"
                    type="number"
                    placeholder="e.g., 0.5"
                    value={jobData.ut_readings}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateJobData("ut_readings", e.target.value)}
                    endAdornment={
                      <Typography variant="text-small" weight="regular" className="text-gray-400">
                        inches
                      </Typography>
                    }
                  />
                  <TextInput
                    label="Extra Layers"
                    type="number"
                    placeholder="e.g., 2"
                    value={jobData.extra_layers}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateJobData("extra_layers", e.target.value)}
                  />
                </>
              )}
            </div>
          </div>

          {/* Design Conditions */}
          <div className="rounded border bg-white p-4">
            <Typography variant="text-small" weight="medium" className="mb-3">
              Design Conditions
            </Typography>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <TextInput
                label="Design Pressure"
                type="number"
                placeholder="e.g., 1000"
                value={designData.design_pressure}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateDesignData("design_pressure", e.target.value)}
                endAdornment={
                  <Typography variant="text-small" weight="regular" className="text-gray-400">
                    psi
                  </Typography>
                }
              />
              <TextInput
                label="Design Temperature"
                type="number"
                placeholder="e.g., 200"
                value={designData.design_temperature}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateDesignData("design_temperature", e.target.value)}
              />
              <div>
                <Typography variant="text-small" weight="medium" className="mb-1">
                  Unit for Design Temperature
                </Typography>
                <Select
                  value={designData.unit_for_design_temperature}
                  onValueChange={(v) => updateDesignData("unit_for_design_temperature", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {temperatureUnitsOptions.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Component Details */}
          <div className="rounded border bg-white p-4">
            <Typography variant="text-small" weight="medium" className="mb-3">
              Component Details
            </Typography>
            <Typography className="mb-2">Please check any that can be applied</Typography>
            <div className="my-4 flex flex-col gap-2 border-b border-b-gray-200 border-solid pb-4">
              {checkOptions.map((o) => (
                <Checkbox
                  key={o.value}
                  checked={componentOptions.includes(o.value)}
                  onChange={(value) => {
                    setComponentOptions((prev) => {
                      if (value && o.value === "none") {
                        return ["none"];
                      } else {
                        return value
                          ? [...prev, o.value].filter((v) => v !== "none")
                          : prev.filter((v) => v !== o.value);
                      }
                    });
                  }}
                  label={o.label}
                />
              ))}
            </div>
            <div className="mb-4">
              <Typography variant="text-small" weight="medium" className="mb-1">
                Component Material
              </Typography>
              <Select value={componentMaterial} onValueChange={setComponentMaterial}>
                <SelectTrigger>
                  <SelectValue placeholder="Component Material" />
                </SelectTrigger>
                <SelectContent>
                  {componentMaterialGroups.map((group, groupIndex) => (
                    <Fragment key={group.group}>
                      {groupIndex > 0 && <SelectSeparator />}
                      <SelectGroup>
                        <SelectLabel>{group.group}</SelectLabel>
                        {group.options.map((option) => {
                          const descriptor = option.type ?? option.general_material;
                          const label = descriptor ? `${option.material} - ${descriptor}` : option.material;
                          return (
                            <SelectItem key={option.id} value={String(option.id)}>
                              {label}
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    </Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {components.map((comp, index) => {
              const scheduleOptions =
                comp.component_nps && comp.component_nps !== "custom" ? getScheduleOptions(comp.component_nps) : [];
              return (
                <div key={index} className="mb-4 rounded border bg-gray-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <Typography variant="text-small" weight="medium">
                      Component {index + 1}
                    </Typography>
                    {components.length > 1 && (
                      <Button variant="outline" size="sm" onClick={() => removeComponent(index)}>
                        Remove
                      </Button>
                    )}
                  </div>
                  <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Checkbox
                      checked={comp.is_flanges}
                      onChange={(checked) => {
                        updateComponent(index, "is_flanges", checked);
                        if (checked) {
                          updateComponent(index, "component_nps", "custom");
                          updateComponent(index, "component_schedule", "");
                        }
                      }}
                      label="Flanges"
                    />
                    <div>
                      <Typography variant="text-small" weight="medium" className="mb-1">
                        NPS
                      </Typography>
                      <Select
                        value={comp.component_nps}
                        onValueChange={(option) => {
                          updateComponent(index, "component_nps", option);
                          if (option === "custom") {
                            updateComponent(index, "component_schedule", "");
                          } else {
                            updateComponent(index, "component_diameter", "");
                            updateComponent(index, "unit_for_component_diameter", "Inches");
                            updateComponent(index, "component_thickness", "");
                            updateComponent(index, "unit_for_component_thickness", "Inches");
                            updateComponent(index, "component_schedule", "");
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select NPS" />
                        </SelectTrigger>
                        <SelectContent>
                          {npsWithCustom.map((option) => (
                            <SelectItem key={String(option.value)} value={String(option.value)}>
                              {option.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {comp.component_nps === "custom" ? (
                    <>
                      <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <TextInput
                          label="Component Diameter"
                          type="number"
                          placeholder="e.g., 10"
                          value={comp.component_diameter || ""}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            updateComponent(index, "component_diameter", e.target.value)
                          }
                        />
                        <div>
                          <Typography variant="text-small" weight="medium" className="mb-1">
                            Unit for Diameter
                          </Typography>
                          <Select
                            value={comp.unit_for_component_diameter}
                            onValueChange={(v) => updateComponent(index, "unit_for_component_diameter", v)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select unit" />
                            </SelectTrigger>
                            <SelectContent>
                              {measureUnitOptions.map((unit) => (
                                <SelectItem key={unit.value} value={unit.value}>
                                  {unit.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {!comp.is_flanges && (
                        <div className="mb-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                          <TextInput
                            label="Component Thickness"
                            type="number"
                            placeholder="e.g., 0.5"
                            value={comp.component_thickness || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              updateComponent(index, "component_thickness", e.target.value)
                            }
                          />
                          <div>
                            <Typography variant="text-small" weight="medium" className="mb-1">
                              Unit for Thickness
                            </Typography>
                            <Select
                              value={comp.unit_for_component_thickness}
                              onValueChange={(v) => updateComponent(index, "unit_for_component_thickness", v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {measureUnitOptions.map((unit) => (
                                  <SelectItem key={unit.value} value={unit.value}>
                                    {unit.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    comp.component_nps && (
                      <div className="mb-3">
                        <Typography variant="text-small" weight="medium" className="mb-1">
                          Schedule
                        </Typography>
                        <Select
                          value={comp.component_schedule || ""}
                          onValueChange={(v) => updateComponent(index, "component_schedule", v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select schedule" />
                          </SelectTrigger>
                          <SelectContent>
                            {scheduleOptions.map((option) => (
                              <SelectItem key={String(option.value)} value={String(option.value)}>
                                {option.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )
                  )}
                  {/* Defect Details embedded per component */}
                  <Typography variant="text-small" weight="medium" className="mb-2">
                    Defect Details for Component {index + 1}
                  </Typography>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <TextInput
                      label="Total Defect Length"
                      type="number"
                      placeholder="e.g., 12"
                      value={defects[index].defect_length_total}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateDefect(index, "defect_length_total", e.target.value)
                      }
                    />
                    <div>
                      <Typography variant="text-small" weight="medium" className="mb-1">
                        Unit
                      </Typography>
                      <Select
                        value={defects[index].unit_for_defect_length}
                        onValueChange={(v) => updateDefect(index, "unit_for_defect_length", v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {measureUnitOptions.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <TextInput
                      label="Wrap Length"
                      type="number"
                      placeholder="e.g., 18"
                      value={defects[index].defect_length_wrap}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        updateDefect(index, "defect_length_wrap", e.target.value)
                      }
                    />
                    <div>
                      <Typography variant="text-small" weight="medium" className="mb-1">
                        Unit
                      </Typography>
                      <Select
                        value={defects[index].unit_for_defect_length_wrap}
                        onValueChange={(v) => updateDefect(index, "unit_for_defect_length_wrap", v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {measureUnitOptions.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              );
            })}
            <Button variant="outline" onClick={addComponent} className="mb-4">
              + Add Component
            </Button>
          </div>

          {/* Repair Options Section */}
          <div className="rounded border bg-white p-4">
            <Typography variant="text-small" weight="medium" className="mb-3">
              Repair Options
            </Typography>
            <Typography variant="text-small" className="mb-4 text-gray-600">
              Check all or ones you would like to add if any
            </Typography>
            <div className="scrollbar-hide mb-4 flex max-h-[400px] flex-col gap-4 overflow-y-auto">
              {groupedRepairOptions.map(([key, options], index) => (
                <div key={key}>
                  <Typography className="mb-2 flex w-full cursor-pointer items-center justify-between gap-2">
                    {index + 1}. {key}
                  </Typography>
                  <div className="flex flex-col gap-2 pl-4">
                    {options.map((option: { id: number; name: string; type: string }) => {
                      function handleRepairOptionChange(value: boolean) {
                        setRepair_length_options((prev) => {
                          if (key.toLowerCase() === "custom") {
                            return value ? [...prev, option.name] : prev.filter((v) => v !== option.name);
                          } else {
                            const otherSelected = prev.filter((v) => !options.some((opt: any) => opt.name === v));
                            return value ? [...otherSelected, option.name] : otherSelected;
                          }
                        });
                      }

                      return (
                        <Checkbox
                          key={option.id}
                          checked={repair_length_options.includes(option.name)}
                          onChange={handleRepairOptionChange}
                          label={option.name}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <Typography variant="text-small" weight="semibold" className="mb-4 text-primary-500">
              At least one kit is required
            </Typography>
            <div className="mb-4 flex flex-col gap-4">
              {kitsOptions.map((kit: { value: number; title: string }) => (
                <Checkbox
                  key={kit.value}
                  checked={repair_length_repair_kit_sizes.includes(kit.value)}
                  onChange={(value) => {
                    setRepair_length_repair_kit_sizes((prev) =>
                      value ? [...prev, kit.value] : prev.filter((v) => v !== kit.value)
                    );
                  }}
                  label={kit.title}
                />
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="rounded border bg-white p-4">
            <Typography variant="text-small" weight="medium" className="mb-3">
              Notes
            </Typography>
            <Label className="mb-2">Additional notes</Label>
            <MultiSelect value={additional_notes} options={mappedNotesOptions} onValueChange={setAdditional_notes} />
            {noteComponents.map((note) => (
              <NoteComponent
                key={note.id}
                data={note}
                onSave={(data) => handleNoteChange(note.id, data)}
                onDelete={() => handleNoteDelete(note.id)}
                allNotesNames={allNotesNames}
              />
            ))}
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={handleAddNote}>
                + Add Note
              </Button>
            </div>
          </div>

          {/* Attachments - Always Visible */}
          <div className="rounded border bg-white p-4">
            <Typography variant="text-small" weight="medium" className="mb-3">
              Attachments
            </Typography>
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="flex-1">
                <Typography variant="text-small" weight="medium" className="mb-2">
                  Attach to report
                </Typography>
                <div className="mb-2 flex flex-wrap gap-4">
                  {documents.map((d) => (
                    <AttachedDocument
                      key={d.name}
                      document={d}
                      onDelete={() => setDocuments((prev) => prev.filter((p) => p.name !== d.name))}
                    />
                  ))}
                </div>
                <FileUploader
                  withPreview={false}
                  setFile={(file) => setDocuments((prev) => [...prev, file])}
                  description="PNG,JPG,PDF, DOCX"
                  fileTypesRegex={/\.(jpe?g|png|pdf|docx)$/i}
                  inputAcceptFiles="image/png, image/jpeg, image/gif, image/svg+xml, application/pdf, application/msword, text/plain, .docx"
                />
              </div>
              <div className="flex-1 md:border-l md:pl-4">
                <Typography variant="text-small" weight="medium" className="mb-2">
                  Reference Documents
                </Typography>
                <div className="mb-2 flex flex-wrap gap-4">
                  {referenceDocuments.map((d) => (
                    <AttachedDocument
                      key={d.name}
                      document={d}
                      onDelete={() => setReferenceDocuments((prev) => prev.filter((p) => p.name !== d.name))}
                    />
                  ))}
                </div>
                <FileUploader
                  withPreview={false}
                  setFile={(file) => setReferenceDocuments((prev) => [...prev, file])}
                  description="PNG,JPG,PDF, DOCX"
                  fileTypesRegex={/\.(jpe?g|png|pdf|docx)$/i}
                  inputAcceptFiles="image/png, image/jpeg, image/gif, image/svg+xml, application/pdf, application/msword, text/plain, .docx"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right sidebar for optional defects */}
        <div className="flex-shrink-0 space-y-4 lg:sticky lg:top-6 lg:w-80">
          <Typography variant="text-md" weight="medium" className="mb-3">
            Optional Defects (Multi-select)
          </Typography>
          <div className="space-y-3 rounded-lg border bg-white p-4">
            {PLACEHOLDER_ITEMS.map((item) => {
              const isSelected = selectedItems.includes(item.id);
              const isDisabled = isNonCompliantSelected && item.id !== "nonCompliant";
              const data = inputValues[item.id] || {};
              return (
                <div
                  key={item.id}
                  className={cn(
                    "space-y-0 overflow-hidden rounded border",
                    isDisabled && "cursor-not-allowed opacity-50"
                  )}
                >
                  {/* biome-ignore lint/a11y/noLabelWithoutControl: Checkbox renders its own <label>+<input>; this outer label preserves click-anywhere-on-row toggle behavior and predates this change. */}
                  <label
                    className={cn(
                      "flex w-full items-center gap-2 border-b p-3 hover:bg-gray-100",
                      isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={(checked: boolean) => toggleItem(item.id, checked)}
                      disabled={isDisabled}
                    />
                    <span className={cn("flex-1", isDisabled && "text-gray-400")}>{item.label}</span>
                  </label>
                  {isSelected && !isDisabled && (
                    <div className="space-y-4 rounded-b bg-gray-50 p-3">
                      {item.id === "pinhole" && (
                        <TextInput
                          label="Pinhole Diameter"
                          type="number"
                          placeholder="e.g., 0.25"
                          value={data.pinhole_diameter || ""}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            updateInput(item.id, "pinhole_diameter", e.target.value)
                          }
                          endAdornment={
                            <Typography variant="text-small" weight="regular" className="text-gray-400">
                              Inches
                            </Typography>
                          }
                        />
                      )}
                      {item.id === "slot" && (
                        <TextInput
                          label="Slot Diameter"
                          type="number"
                          placeholder="e.g., 1"
                          value={data.slot_diameter || ""}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            updateInput(item.id, "slot_diameter", e.target.value)
                          }
                          endAdornment={
                            <Typography variant="text-small" weight="regular" className="text-gray-400">
                              Inches
                            </Typography>
                          }
                        />
                      )}
                      {item.id === "burial" && (
                        <div className="space-y-4">
                          <Typography variant="text-small" weight="medium" className="mb-3">
                            Burial Loads
                          </Typography>
                          <div className="flex flex-col gap-4">
                            <TextInput
                              label="Buried Depth"
                              type="number"
                              placeholder="e.g., 24"
                              value={data.buried_depth || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "buried_depth", e.target.value)
                              }
                            />
                            <div>
                              <Typography variant="text-small" weight="medium" className="mb-1">
                                Unit
                              </Typography>
                              <Select
                                value={data.unit_for_buried_check || "Inches"}
                                onValueChange={(v) => updateInput(item.id, "unit_for_buried_check", v)}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select unit" />
                                </SelectTrigger>
                                <SelectContent>
                                  {measureUnitOptions.map((unit) => (
                                    <SelectItem key={unit.value} value={unit.value}>
                                      {unit.title}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                      )}
                      {item.id === "axialLoads" && (
                        <Typography variant="text-small" className="text-gray-600">
                          Axial load design enabled (straight line component assumed).
                        </Typography>
                      )}
                      {item.id === "additionalForces" && (
                        <div className="space-y-4">
                          <Typography variant="text-small" weight="medium" className="mb-3">
                            Additional Forces and Moments
                          </Typography>
                          <div className="flex flex-col gap-4">
                            <TextInput
                              label="Applied Sheared Load"
                              type="number"
                              placeholder="e.g., 1000"
                              value={data.applied_sheared_load || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "applied_sheared_load", e.target.value)
                              }
                              endAdornment={
                                <Typography variant="text-small" weight="regular" className="text-gray-400">
                                  lbf
                                </Typography>
                              }
                            />
                            <TextInput
                              label="Applied Axial Load"
                              type="number"
                              placeholder="e.g., 1000"
                              value={data.applied_axial_load || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "applied_axial_load", e.target.value)
                              }
                              endAdornment={
                                <Typography variant="text-small" weight="regular" className="text-gray-400">
                                  lbf
                                </Typography>
                              }
                            />
                            <TextInput
                              label="Applied Tortional Moment"
                              type="number"
                              placeholder="e.g., 500"
                              value={data.applied_tortional_moment || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "applied_tortional_moment", e.target.value)
                              }
                              endAdornment={
                                <Typography variant="text-small" weight="regular" className="text-gray-400">
                                  ft*lbf
                                </Typography>
                              }
                            />
                            <TextInput
                              label="Applied Axial Moment"
                              type="number"
                              placeholder="e.g., 500"
                              value={data.applied_axial_moment || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "applied_axial_moment", e.target.value)
                              }
                              endAdornment={
                                <Typography variant="text-small" weight="regular" className="text-gray-400">
                                  ft*lbf
                                </Typography>
                              }
                            />
                          </div>
                        </div>
                      )}
                      {item.id === "externalPressure" && (
                        <TextInput
                          label="External Pressure Vacuum"
                          type="number"
                          placeholder="e.g., 14.7"
                          value={data.external_pressure_vacuum || ""}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            updateInput(item.id, "external_pressure_vacuum", e.target.value)
                          }
                          endAdornment={
                            <Typography variant="text-small" weight="regular" className="text-gray-400">
                              PSI
                            </Typography>
                          }
                        />
                      )}
                      {item.id === "pressureCycles" && (
                        <div className="space-y-4">
                          <Typography variant="text-small" weight="medium" className="mb-3">
                            Pressure Cycles
                          </Typography>
                          <div className="flex flex-col gap-4">
                            <TextInput
                              label="Number of Cycles"
                              type="number"
                              placeholder="e.g., 10000"
                              value={data.number_of_cycles || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "number_of_cycles", e.target.value)
                              }
                            />
                            <TextInput
                              label="Min Cycle Pressure"
                              type="number"
                              placeholder="e.g., 500"
                              value={data.min_cycle_pressure || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "min_cycle_pressure", e.target.value)
                              }
                              endAdornment={
                                <Typography variant="text-small" weight="regular" className="text-gray-400">
                                  PSI
                                </Typography>
                              }
                            />
                            <TextInput
                              label="Max Cycle Pressure"
                              type="number"
                              placeholder="e.g., 1000"
                              value={data.max_cycle_pressure || ""}
                              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                updateInput(item.id, "max_cycle_pressure", e.target.value)
                              }
                              endAdornment={
                                <Typography variant="text-small" weight="regular" className="text-gray-400">
                                  PSI
                                </Typography>
                              }
                            />
                          </div>
                        </div>
                      )}
                      {item.id === "continuousStrains" && (
                        <Typography variant="text-small" className="text-gray-600">
                          Design for continuous long term strains enabled.
                        </Typography>
                      )}
                      {item.id === "operatingConditions" && (
                        <div className="space-y-4">
                          <TextInput
                            label="Operating Pressure"
                            type="number"
                            placeholder="e.g., 800"
                            value={data.operating_pressure || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              updateInput(item.id, "operating_pressure", e.target.value)
                            }
                            endAdornment={
                              <Typography variant="text-small" weight="regular" className="text-gray-400">
                                psi
                              </Typography>
                            }
                          />
                          <TextInput
                            label="Operating Temperature"
                            type="number"
                            placeholder="e.g., 150"
                            value={data.operating_temperature || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              updateInput(item.id, "operating_temperature", e.target.value)
                            }
                          />
                          <div>
                            <Typography variant="text-small" weight="medium" className="mb-1">
                              Unit for Operating Temperature
                            </Typography>
                            <Select
                              value={data.unit_for_operating_temperature || "Fahrenheit"}
                              onValueChange={(v) => updateInput(item.id, "unit_for_operating_temperature", v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {temperatureUnitsOptions.map((unit) => (
                                  <SelectItem key={unit.value} value={unit.value}>
                                    {unit.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <TextInput
                            label="Installation Temperature"
                            type="number"
                            placeholder="e.g., 70"
                            value={data.installation_temperature || ""}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              updateInput(item.id, "installation_temperature", e.target.value)
                            }
                          />
                          <div>
                            <Typography variant="text-small" weight="medium" className="mb-1">
                              Unit for Installation Temperature
                            </Typography>
                            <Select
                              value={data.unit_for_installation_temperature || "Fahrenheit"}
                              onValueChange={(v) => updateInput(item.id, "unit_for_installation_temperature", v)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {temperatureUnitsOptions.map((unit) => (
                                  <SelectItem key={unit.value} value={unit.value}>
                                    {unit.title}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                      {item.id === "excludeComponentStrength" && (
                        <Typography variant="text-small" className="text-gray-600">
                          Completely exclude component strength for design (non-standard, results in most layers).
                        </Typography>
                      )}
                      {item.id === "nonCompliant" && (
                        <TextInput
                          label="Desired Layer Count"
                          type="number"
                          placeholder="e.g., 2"
                          value={data.desired_layer_count || ""}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            updateInput(item.id, "desired_layer_count", e.target.value)
                          }
                          endAdornment={
                            <Typography variant="text-small" weight="regular" className="text-gray-400">
                              layers
                            </Typography>
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-8 flex justify-center gap-4">
        <Button variant="outline" onClick={handleCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleSaveDraft} disabled={isPending || !isValidForDraft()} size="lg">
          {isPending ? "Saving..." : "Save as draft"}
        </Button>
        {canRunCalculations && (
          <Button onClick={handleRunCalculations} disabled={isPending || !isValidForSubmit()} size="lg">
            {isPending ? "Running..." : "Run calculations"}
          </Button>
        )}
      </div>
    </div>
  );
}
